import React from 'react';

import FileInput from 'react-fine-uploader/file-input';
import {connect, type DefaultRootState} from 'react-redux';
import {
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledButtonDropdown,
} from 'reactstrap';
import {v4 as uuid} from 'uuid';

import {AttachOverlay} from 'app/general/overlays';
import FineUploaderFileList from 'app/general/widgets/FineUploaderFileList';
import {accountSettingsSelector} from 'app/settings/api';
import {showNotification} from 'app/toasts/slice';
import {generateImageDataFromEmbedly} from 'app/utils/embedly';
import {
  getErrorMessage,
  preparedUploader,
  removeMediaPath,
} from 'app/utils/fineuploader';
import {gt} from 'app/utils/i18n';

import {EmbedVideoPopover} from './EmbedVideoPopover';

import type {
  AccountSettingsSerializedType,
  AttachedFile,
  ChoicesStepFieldType,
  S3UploadConfigType,
  StepFieldType,
} from 'sweet-types';

interface OwnProps {
  onAddFile?: (file: AttachedFile) => void;
  onAddIframe?: (iframe: string) => void;
  onAddVideo?: (video: any) => void;
  onAddField?: (field: StepFieldType) => void;
  onBusy: (id: number, isBusy: boolean) => void;
  hideFileUpload?: boolean;
  hideAddField?: boolean;
  hideEmbedVideo?: boolean;
}

interface StateProps
  extends Pick<
    AccountSettingsSerializedType,
    | 'EMBEDLY_KEY'
    | 'STATIC_URL'
    | 'account_id'
    | 'MEDIA_URL'
    | 'can_upload_files'
  > {
  s3Upload: S3UploadConfigType;
}

interface DispatchProps {
  showNotification: typeof showNotification;
}

interface Props extends OwnProps, StateProps, DispatchProps {}

interface State {
  submittedFiles: Array<number>;
}

class BaseStepEmbedOptions extends React.Component<Props, State> {
  state: State = {
    submittedFiles: [],
  };
  uploader: any;

  constructor(props: Props) {
    super(props);
    this.uploader = this.buildUploader();
  }

  UNSAFE_componentWillMount() {
    this.uploader.on('submitted', this.handleSubmitted);
    this.uploader.on('complete', this.handleComplete);
    this.uploader.on('error', this.handleError);
  }

  componentWillUnmount() {
    this.uploader.off('submitted', this.handleSubmitted);
    this.uploader.off('complete', this.handleComplete);
    this.uploader.off('error', this.handleError);
  }

  handleError = (id: any, file: string, code: string) => {
    this.props.showNotification(
      getErrorMessage(this.uploader, code),
      'error',
      7000
    );
  };

  handleSubmitted = (id: number): void => {
    this.setState({
      submittedFiles: this.state.submittedFiles.concat([id]),
    });
    this.props.onBusy(id, true);
  };
  handleComplete = (id: number, name: string, response: any): void => {
    const {onAddFile} = this.props;
    this.props.onBusy(id, false);

    if (response.success === true && onAddFile != null) {
      onAddFile({
        file_ref: removeMediaPath(
          this.uploader.methods.getKey(id),
          this.props.MEDIA_URL
        ),
        file_name: name,
        file_type: name.split('.').pop()?.toLowerCase() ?? '',
      });
    }
  };

  buildUploader() {
    const {account_id, MEDIA_URL, s3Upload} = this.props;
    return preparedUploader(s3Upload, account_id, MEDIA_URL);
  }

  cancelUploadFile = (id: number): void => {
    this.setState({
      submittedFiles: this.state.submittedFiles.filter(
        (fileId) => fileId !== id
      ),
    });
  };
  addField = (type: StepFieldType['type']) => {
    const {onAddField} = this.props;
    let fieldData;

    if (onAddField != null) {
      if (type === 'radio' || type === 'checkbox') {
        fieldData = {
          label: '',
          type: type,
          id: uuid(),
          choices: [
            {
              label: '',
              id: uuid(),
            },
            {
              label: '',
              id: uuid(),
            },
          ],
        } as ChoicesStepFieldType;
      } else {
        fieldData = {
          label: '',
          type: type,
          id: uuid(),
        } as StepFieldType;
      }

      onAddField(fieldData);
    }
  };
  addVideo = (videoURL: string): Promise<any> => {
    // accept a videoURL or an embed code.
    const {EMBEDLY_KEY, STATIC_URL, onAddVideo, onAddIframe, showNotification} =
      this.props;

    if (videoURL.trim().startsWith('<iframe')) {
      if (onAddIframe != null) {
        return Promise.resolve(onAddIframe(videoURL));
      }
    }

    return generateImageDataFromEmbedly(
      videoURL,
      EMBEDLY_KEY,
      {
        frame: true,
        maxwidth: 320,
        autoplay: true,
      },
      STATIC_URL
    ).then(
      (image) => {
        if (onAddVideo != null) {
          return onAddVideo(image);
        }

        return image;
      },
      (error) => {
        showNotification(error.toString(), 'error');
        throw error;
      }
    );
  };

  render() {
    const {hideAddField, hideFileUpload, can_upload_files, hideEmbedVideo} =
      this.props;
    return (
      <div className="float-left">
        {can_upload_files ? (
          <FineUploaderFileList
            uploader={this.uploader}
            files={this.state.submittedFiles}
            onCancelFile={this.cancelUploadFile}
          />
        ) : null}
        {hideEmbedVideo !== true ? (
          <AttachOverlay
            trigger="click legacy"
            overlay={({overlayProps}) => (
              <EmbedVideoPopover
                addVideo={this.addVideo}
                overlayProps={{...overlayProps, placement: 'bottom'}}
              />
            )}
          >
            {(handlers) => (
              <button className="btn btn-sm btn-link" {...handlers}>
                <i className="fa fa-video" /> {gt('Embed Code')}
              </button>
            )}
          </AttachOverlay>
        ) : null}

        {hideAddField !== true ? (
          <UncontrolledButtonDropdown>
            <DropdownToggle color="link" className="btn-sm" caret>
              <i className="fa fa-pen-square" /> {gt('Add Form Field')}
            </DropdownToggle>
            <DropdownMenu>
              <DropdownItem key="text" onClick={() => this.addField('text')}>
                {gt('Text Field')}
              </DropdownItem>
              <DropdownItem
                key="textarea"
                onClick={() => this.addField('textarea')}
              >
                {gt('Long Text Field')}
              </DropdownItem>
              <DropdownItem
                key="checkbox"
                onClick={() => this.addField('checkbox')}
              >
                {gt('Checkbox Field')}
              </DropdownItem>
              <DropdownItem key="radio" onClick={() => this.addField('radio')}>
                {gt('Radio Field')}
              </DropdownItem>
              <DropdownItem key="date" onClick={() => this.addField('date')}>
                {gt('Date Field')}
              </DropdownItem>
              {can_upload_files && hideFileUpload !== true ? (
                <DropdownItem key="file" onClick={() => this.addField('file')}>
                  {gt('Attach File')}
                </DropdownItem>
              ) : null}
            </DropdownMenu>
          </UncontrolledButtonDropdown>
        ) : null}

        {can_upload_files && hideFileUpload !== true ? (
          <FileInput
            uploader={this.uploader}
            className="btn btn-sm btn-link collapsed"
          >
            <i className="far fa-file" /> {gt('Attach File')}
          </FileInput>
        ) : null}
      </div>
    );
  }
}

const mapStateToProps = (state: DefaultRootState): StateProps => {
  const settings = accountSettingsSelector(state);

  const {
    EMBEDLY_KEY,
    STATIC_URL,
    account_id,
    MEDIA_URL,
    can_upload_files,
    s3Upload,
  } = settings.data!;

  return {
    EMBEDLY_KEY,
    STATIC_URL,
    account_id,
    MEDIA_URL,
    can_upload_files,
    s3Upload: s3Upload.FILES,
  };
};

export const StepEmbedOptions = connect<StateProps, DispatchProps, OwnProps>(
  mapStateToProps,
  {
    showNotification,
  }
)(BaseStepEmbedOptions);
